import { Component, ElementRef, inject, OnInit, ViewChild } from '@angular/core';

import { AsyncPipe } from '@angular/common';
import { NxButtonModule } from '@aposin/ng-aquila/button';
import { NxMessageModule, NxMessageToastService } from '@aposin/ng-aquila/message';
import { NxModalModule } from '@aposin/ng-aquila/modal';
import { NxPopoverModule } from '@aposin/ng-aquila/popover';
import { NxSpinnerModule } from '@aposin/ng-aquila/spinner';
import { FormsModule } from '@angular/forms';
import { NxDropdownModule } from '@aposin/ng-aquila/dropdown';
import { NxIconModule } from '@aposin/ng-aquila/icon';
import { NxInputModule } from '@aposin/ng-aquila/input';
import { NxPaginationModule } from '@aposin/ng-aquila/pagination';
import { NxTableModule } from '@aposin/ng-aquila/table';
import { take } from 'rxjs';
import { Application } from '../../models/application.model';
import { Feature } from '../../models/feature.model';
import { ServiceConfig } from '../../models/service-config.model';
import { ApplicationService } from '../../services/application.service';
import { FeatureService } from '../../services/feature.service';
import { MockDataService } from '../../services/mock-data.service';
import { PermissionService } from '../../services/permission.service';
import { ServiceConfigService } from '../../services/service-config.service';
import { isLocalEnvironment } from '../../utils/environment.utils';
import { DarkModeModule } from "../dark-mode/dark-mode.module";
import { ApplicationFormComponent } from './application-form/application-form.component';
import { ApplicationTableComponent } from './application-table/application-table.component';
import { FeatureFormComponent } from './feature-form/feature-form.component';
import { ServiceConfigFormComponent } from './service-config-form/service-config-form.component';

@Component({
  selector: 'app-service-config',
  standalone: true,
  imports: [
    NxTableModule,
    NxSpinnerModule,
    NxMessageModule,
    NxButtonModule,
    NxModalModule,
    ServiceConfigFormComponent,
    ApplicationFormComponent,
    ApplicationTableComponent,
    FeatureFormComponent,
    AsyncPipe,
    NxPopoverModule,
    NxPaginationModule,
    NxDropdownModule,
    NxInputModule,
    NxIconModule,
    FormsModule,
    DarkModeModule,
  ],
  templateUrl: './service-config.component.html',
  styleUrl: './service-config.component.scss'
})
export class ServiceConfigComponent implements OnInit {
  @ViewChild(ApplicationTableComponent) applicationTableComponent?: ApplicationTableComponent;

  private readonly serviceConfigService = inject(ServiceConfigService);
  private readonly applicationService = inject(ApplicationService);
  private readonly featureService = inject(FeatureService);
  private readonly mockDataService = inject(MockDataService);
  private readonly messageToastService = inject(NxMessageToastService);
   permissionService = inject(PermissionService);

  readonly perPageOptions = [15, 20, 25, 30, 35, 40, 50];

  serviceConfigs: ServiceConfig[] = [];
  currentPage = 1;
  perPage = 20;

  hasHorizontalOverflow = false;
  @ViewChild('tableWrapper') tableWrapperEl?: ElementRef<HTMLElement>;

  selectedTeamFilter: string | null = null;
  teamFilterSearch = '';

  serviceFilterSearch = '';

  get teamOptions(): string[] {
    const teams = this.serviceConfigs
      .map(sc => sc.pagerDutyTeamName || sc.pagerDutyTeamId)
      .filter((t): t is string => !!t);
    return [...new Set(teams)].sort();
  }

  get filteredTeamOptions(): string[] {
    const q = this.teamFilterSearch.toLowerCase();
    return q ? this.teamOptions.filter(t => t.toLowerCase().includes(q)) : this.teamOptions;
  }

  get filteredServiceConfigs(): ServiceConfig[] {
    return this.serviceConfigs.filter(sc => {
      const teamMatch = !this.selectedTeamFilter ||
        (sc.pagerDutyTeamName || sc.pagerDutyTeamId) === this.selectedTeamFilter;
      const serviceMatch = !this.serviceFilterSearch ||
        sc.pagerDutyServiceName?.toLowerCase().includes(this.serviceFilterSearch.toLowerCase());
      return teamMatch && serviceMatch;
    });
  }

  get pagedServiceConfigs(): ServiceConfig[] {
    const start = (this.currentPage - 1) * this.perPage;
    return this.filteredServiceConfigs.slice(start, start + this.perPage);
  }

  onTableScroll(event: Event): void {
    const el = event.target as HTMLElement;
    this.hasHorizontalOverflow = el.scrollLeft + el.clientWidth < el.scrollWidth - 1;
  }

  checkHorizontalOverflow(): void {
    setTimeout(() => {
      const el = this.tableWrapperEl?.nativeElement;
      if (el) this.hasHorizontalOverflow = el.scrollWidth > el.clientWidth;
    });
  }

  onPageChange(page: number): void {
    this.currentPage = page;
    this.selectedServiceId = null;
  }

  onPerPageChange(value: number): void {
    this.perPage = value;
    this.currentPage = 1;
    this.selectedServiceId = null;
  }

  onTeamFilterChange(team: string | null): void {
    this.selectedTeamFilter = team;
    this.currentPage = 1;
    this.selectedServiceId = null;
  }

  clearTeamFilter(): void {
    this.selectedTeamFilter = null;
    this.teamFilterSearch = '';
    this.currentPage = 1;
    this.selectedServiceId = null;
  }

  onServiceSearchChange(): void {
    this.currentPage = 1;
    this.selectedServiceId = null;
  }

  clearServiceFilter(): void {
    this.serviceFilterSearch = '';
    this.currentPage = 1;
    this.selectedServiceId = null;
  }

  selectedServiceId: string | null = null;
  currentServiceConfigId: string | null = null;
  currentApplicationId: string | null = null;
  currentFeatureId: string | null = null;
  isServiceConfigsLoading = false;
  showApplicationForm = false;
  showFeatureForm = false;

  applicationToDelete: Application | null = null;
  showApplicationDeleteModal = false;
  isDeletingApplication = false;

  featureToDelete: Feature | null = null;
  showFeatureDeleteModal = false;
  isDeletingFeature = false;

  isPopoverOpen = false;

  onPopoverToggle(event: unknown): void {
    let isOpen: boolean;

    if (typeof event === 'boolean') {
      isOpen = event;
    } else if (event && typeof event === 'object') {
      const eventObj = event as Record<string, unknown>;
      isOpen = !!(eventObj['opened'] || eventObj['value'] || eventObj['isOpen']);
    } else {
      isOpen = false;
    }
    this.isPopoverOpen = isOpen;
  }

  onButtonClick(): void {
    this.isPopoverOpen = !this.isPopoverOpen;
  }

  ngOnInit(): void {
    this.initData();
  }

  initData() {
    this.isServiceConfigsLoading = true;

    const dataService = isLocalEnvironment()
      ? this.mockDataService.getServiceConfigs()
      : this.serviceConfigService.getServiceConfigs();

    dataService
      .pipe(take(1))
      .subscribe((response) => {
        this.serviceConfigs = response?.body || [];
        this.currentPage = 1;
        this.isServiceConfigsLoading = false;
        this.checkHorizontalOverflow();
      });
  }

 
  setCurrentServiceConfig(serviceConfig: ServiceConfig) {
    this.currentServiceConfigId = serviceConfig.pagerDutyServiceId;
  }

  onServiceSelect(serviceConfig: ServiceConfig) {
    if (this.selectedServiceId === serviceConfig.pagerDutyServiceId) {
      this.selectedServiceId = null;
    } else {
      this.selectedServiceId = serviceConfig.pagerDutyServiceId;
    }
  }

  onClosePanel(event: { refreshList: boolean; updatedServiceConfig?: ServiceConfig; updatedApplication?: Application, updatedFeature?: Feature }): void {
    this.currentServiceConfigId = null;
    this.currentApplicationId = null;
    this.showApplicationForm = false;
    this.currentFeatureId = null;
    this.showFeatureForm = false;

    if (event.refreshList) {
      if (event.updatedServiceConfig) {
        this.updateServiceConfigInPlace(event.updatedServiceConfig);
      } else if (event.updatedApplication) {
        this.applicationTableComponent?.updateApplicationInPlace(event.updatedApplication);
      } else if (event.updatedFeature) {
        this.applicationTableComponent?.refreshApplications();
      } else {
        this.applicationTableComponent?.refreshApplications();
        this.initData();
      }
    }
  }

  updateServiceConfigInPlace(updatedServiceConfig: ServiceConfig) {
    const index = this.serviceConfigs.findIndex(
      sc => sc.pagerDutyServiceId === updatedServiceConfig.pagerDutyServiceId
    );

    if (index !== -1) {
      this.serviceConfigs[index] = updatedServiceConfig;
      this.serviceConfigs = [...this.serviceConfigs];
    }
  }

  onEditApplication(application: Application) {
    this.currentApplicationId = application.applicationId;
    this.showApplicationForm = true;
  }

  onCreateApplication() {
    this.currentApplicationId = null;
    this.showApplicationForm = true;
  }

  onDeleteApplication(application: Application) {
    if (!this.selectedServiceId) {
      return;
    }
    this.applicationToDelete = application;
    this.showApplicationDeleteModal = true;
  }

  confirmApplicationDelete() {
    if (!this.applicationToDelete || !this.selectedServiceId) {
      return;
    }
    this.deleteApplication(this.applicationToDelete.applicationId);
    this.closeApplicationDeleteModal();
  }

  closeApplicationDeleteModal() {
    this.showApplicationDeleteModal = false;
    this.applicationToDelete = null;
  }

  deleteApplication(applicationId: string) {
    if (!this.selectedServiceId) {
      return;
    }

    this.isDeletingApplication = true;

    const dataService = isLocalEnvironment()
      ? this.mockDataService.deleteApplication(this.selectedServiceId, applicationId)
      : this.applicationService.deleteApplication(this.selectedServiceId, applicationId);

    dataService
      .pipe(take(1))
      .subscribe({
        next: () => {
          this.isDeletingApplication = false;
          this.messageToastService.open('Application deleted successfully', {
            context: 'success',
            duration: 5000,
          });
          this.applicationTableComponent?.refreshApplications();
        },
        error: () => {
          this.isDeletingApplication = false;
          this.messageToastService.open('Failed to delete application', {
            context: 'info',
            duration: 5000,
          });
        }
      });
  }

  onCreateFeature(application: Application) {
    this.currentApplicationId = application.applicationId;
    this.currentFeatureId = null;
    this.showFeatureForm = true;
  }

  onEditFeature(data: { application: Application, featureId: string }) {
    this.currentApplicationId = data.application.applicationId;
    this.currentFeatureId = data.featureId;
    this.showFeatureForm = true;
  }

  onDeleteFeature(data: { application: Application, featureId: string }) {
    if (!this.selectedServiceId) {
      return;
    }
    const feature = data.application.features.find(f => f.featureId === data.featureId);
    if (!feature) return;

    this.featureToDelete = feature;
    this.currentApplicationId = data.application.applicationId;
    this.showFeatureDeleteModal = true;
  }

  confirmFeatureDelete() {
    if (!this.featureToDelete || !this.selectedServiceId || !this.currentApplicationId) {
      return;
    }
    this.deleteFeature(this.currentApplicationId, this.featureToDelete.featureId);
    this.closeFeatureDeleteModal();
  }

  closeFeatureDeleteModal() {
    this.showFeatureDeleteModal = false;
    this.featureToDelete = null;
    this.currentApplicationId = null;
  }

  deleteFeature(applicationId: string, featureId: string) {
    if (!this.selectedServiceId) {
      return;
    }

    this.isDeletingFeature = true;

    const dataService = isLocalEnvironment()
      ? this.mockDataService.deleteFeature(this.selectedServiceId, applicationId, featureId)
      : this.featureService.deleteFeature(this.selectedServiceId, applicationId, featureId);

    dataService
      .pipe(take(1))
      .subscribe({
        next: () => {
          this.isDeletingFeature = false;
          this.messageToastService.open('Feature deleted successfully', {
            context: 'success',
            duration: 5000,
          });
          this.applicationTableComponent?.refreshApplications();
        },
        error: () => {
          this.isDeletingFeature = false;
          this.messageToastService.open('Failed to delete feature', {
            context: 'info',
            duration: 5000,
          });
        }
      });
  }
}
